import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    Inject
} from '@angular/core';
import { first, Observable } from 'rxjs';
import {
    MatDialog,
    MatDialogRef,
    MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LgvModel } from '../../data-models/longevity.model';
import { LgvClientService, LgvUserService } from '../../core/core.module';
import { LgvDataHelper } from '../../core/utility/lgv-data-helper';
import { LgvPricingModel } from '../../features/pricing/models/pricing.model';
import { LgvAuthMgrService } from '../../core/services/lgv-auth-mgr.service';

//export interface InfoPopupData {
//    name: string;
//    value: string;
//}

@Component({
    selector: 'info-popup',
    templateUrl: 'info-popup.component.html',
    styleUrls: ['./info-popup.component.scss']
})
export class InfoPopup {
    //Custom Popups
    get isMarketValueMode(): boolean {
        return this.data && this.data.mode === 'marketValue';
    }
    get isEquityCalculation(): boolean {
        return this.data && this.data.mode === 'equity';
    }
    get isSpeakMortgage(): boolean {
        return this.data && this.data.mode === 'SpeakMortgage';
    }
    get isExpiredProduct(): boolean {
        return this.data && this.data.mode === 'ExpiredProduct';
    }
    get isMonthlyPaymentSavings(): boolean {
        return this.data && this.data.mode === 'MonthlyPaymentSavings';
    }
    get isMonthlyPaymentIncrease(): boolean {
        return this.data && this.data.mode === 'MonthlyPaymentIncrease';
    }
    get isRateAndAPR(): boolean {
        return this.data && this.data.mode === 'RateAndAPR';
    }
    get isGenericMode(): boolean {
        return this.data && this.data.mode === 'generic';
    }
    get isPoints(): boolean {
        return this.data && this.data.mode === 'Points';
    }
    get isEstimatedCosts(): boolean {
        return this.data && this.data.mode === 'EstimatedCosts';
    }
    get isTodaysRate(): boolean {
        return this.data && this.data.mode === 'TodaysRate';
    }
    get isQuoteDetails(): boolean {
        return this.data && this.data.mode === 'QuoteDetails';
    }

    get HaveDictionaryItem(): boolean {
        return (
            this.data &&
            this.data.mode &&
            this.Dictionary[this.data.mode] !== null
        );
    }
    get DictionaryObject(): boolean {
        return this.HaveDictionaryItem ? this.Dictionary[this.data.mode] : null;
    }

    get isArm(): boolean {
        return (
            this.opportunity &&
            this.opportunity.opportunityInfo.amortizationTypeName === 'ARM'
        );
    }
    opportunity: LgvModel.Opportunity;
    product: LgvPricingModel.Product;
    quote: LgvPricingModel.ProductQuoteResult;
    quoteDetail: LgvPricingModel.Quote;
    todaysDate = new Date();

    Dictionary = {
        Principal: {
            Header: 'Principal',
            Body:
                'The principal is the amount of money you owe on your home loan, and the amount shown is how much you have paid on said principal. The more money you pay on the principal will reduce the amount you pay in interest.'
        },
        Interest: {
            Header: 'Interest',
            Body:
                'Interest is the price you pay for borrowing money on your loan.'
        },
        Equity: {
            Header: 'Equity',
            Body:
                'Equity is how much an asset is worth after all debts and liabilities have been paid.'
        },
        Debt: {
            Header: 'Debt',
            Body:
                'Debt is the amount of money, property or service that is owed to someone else.'
        },
        IntVsAPR: {
            Header: 'Mortgage interest vs APR in a nutshell',
            Body:
                'Mortgage interest rate is the advertised cost of borrowing the principal, while the Annual Percentage Rate (APR) is the overall cost of borrowing the money (including the mortgage interest rate the lender charges you). The factors that affect interest rates and APR are different. An interest rate could increase or decrease depending on the lender, market condition, your credit score and the amount of your down payment. APR, on the other hand, could increase or decrease depending on broker fees, closing costs, discount points and any other fees that you need to pay for processing the loan.'
        },
        HomeEquity: {
            Header: 'Home equity explained',
            Body:
                'Home equity is the value of the home that you truly own (versus the amount owed to the lender). For example, if a person buys a home with a fair market value of $300,000 and makes a 20% down payment, the buyer has a $60,000 worth of home equity (because $60,000 is 20% of $300,000). Aside from the down payment and the amount repaid over time, there are other factors that could affect home equity. Improving your home with renovations and paying extra money towards your loan’s principal both increase your equity, while current property prices in your area and other market forces can help or harm the equity of your home.'
        },
        HomeValueEstimate: {
            Header: 'How is my Home Value Estimate calculated?',
            Body:
                'The My Home Value Estimate graph takes your home’s original purchase price, adjusts the amount dynamically for inflation over time, and compares this with MLS data on recently sold comparable homes in your area. (MLS = Multiple Listing Service used by real estate agents.) This estimate doesn’t include values for any improvements you may have done. It is not an appraisal but a computer algorithm, and a more accurate Fair Market Value Assessment can be done with a professional who is more familiar with your neighborhood.'
        },
        SoMuchInterest: {
            Header: 'Why am I paying so much interest?',
            Body:
                'You may notice that you pay more of your loan’s interest than the principal (borrowed amount) in the first years of your loan. Though your monthly payment doesn’t change, over time the portion of your payment that goes toward interest will gradually decrease, and the portion repaying the principal will increase. Adding a small amount to your monthly payment, applied to the principal, can save you money in the long run. Fortunately, you can deduct that mortgage interest on your income taxes (if you itemize when you file)!'
        },
        AddToPmt: {
            Header: 'Why would I want to add to my monthly payment?',
            Body:
                'If you’re planning to stay in your home for a long time, you may want to pay a little extra each month toward your loan principal in order to reduce the total amount of interest you owe over time. (Remember, the amount of interest you pay is based off of your principal balance.) Make sure your mortgage doesn’t have prepayment penalties (most don’t) and that you specify that the extra money is to be applied to your mortgage principal when you pay. Depending on your circumstances, it may be better to pay down other high-interest debts first.'
        },
        RefinanceBasics: {
            Header: 'Refinance basics',
            Body:
                'Refinancing your mortgage could be beneficial in several ways including getting a much lower interest rate (meaning lower monthly payments), mortgage insurance elimination, shortening the mortgage term (so you can pay off your loan sooner), or even changing an adjustable-rate mortgage into a fixed-rate mortgage. Depending on your loan size, closing costs could be around 2–6% of your total loan amount. If you have a rate of 4%+, or you can lower your current rate by 1%+, you should talk to your Loan Advisor about your potential savings. Please note: If a lender offers you a “no-closing cost refinance,” this means you don’t have to pay upfront fees when you refinance your loan, but the lender will either charge you a higher loan interest rate or it will just be added to your principal instead. Learn more about typical mortgage refinance fees.'
        },
        ShouldIRefinance: {
            Header: 'Should I refinance?',
            Body:
                'The first thing you should determine before proceeding with a mortgage refinance: How long do you want to stay in your current home? If you’re improving a house in order to “flip” and sell it in the short term, you could benefit from an ARM (Adjustable Rate Mortgage). If you can sell the house before the rate adjusts upward, your payments will be less than a conventional loan. Despite the enticing low introductory rate, the ARM is not a wise choice for living within a budget because the rate changes cannot be predicted. If you’re planning to stay in your current home for the next five years or so, you could save quite a bit by getting a lower rate, or pay less interest by shortening your loan’s term. Just like your original mortgage, you’ll pay closing costs that run 2–6% of the loan principal. For example, if your new loan saves you $192 per month and your closing costs were $11,000, it would take almost 5 years to recoup (58 payments). If you have a rate at or above 4% or can lower your current rate by 1% or more, it likely makes sense for you to talk to your Loan Advisor to go over the potential savings. If a lender offers you a “no-closing cost refinance,” this means you don’t have to pay upfront fees when you refinance your loan, but the lender will either charge you a higher loan interest rate or it will just be added to your principal instead.'
        },
        CashOutRefi: {
            Header: 'How a cash-out refinance works',
            Body:
                'A cash-out refinance allows for you to refinance your current loan and borrow money all in the same transaction. The new loan replaces your existing mortgage with a larger one, based on your home’s fair market value—allowing you to pocket the remaining amount. This type of refinancing is worth considering if you have a good credit score, a low debt-to-income ratio, and you’ve paid down at least 20% of your original loan.  Funding a college education, debt consolidation, and major home renovation projects are just a few of the ways to utilize your home equity. Making “substantial” home improvements using a cash-out refinance could even lead to tax breaks. Plus, you’ll eventually recoup your closing costs if you don’t sell your home anytime soon.'
        },
        CashOutAmount: {
            Header: 'Cash Out Amount',
            Body: `<p>The cash-out amount in a cash-out refinance refers to the difference between the new mortgage amount and the existing mortgage balance. When homeowners refinance, they take out a new loan for more than they owe on their current mortgage, and the difference is given to them in cash.</p>
                <p>For example, if you owe $200,000 on your current mortgage and refinance for $300,000, your cash-out amount would be $100,000. However, lenders typically allow you to cash out only a certain percentage of your home's equity, often around 80% to 90%, depending on the lender and loan type.</p>
                <p>Keep in mind that the cash-out amount will also depend on the home's current appraised value and any applicable fees associated with the refinance.</p>
                <p>If the client does not need to take cash out, the loan purpose should be "Refi Rate / Term" instead of cash out refi to get better rates.</p>`
        },
        StandAlone2nd: {
            Header: 'Stand-alone second mortgage',
            Body:
                'Instead of refinancing, you can also tap into your home’s accumulated equity with a stand-alone second mortgage. There are two options then for people who want to do a stand-alone second mortgage. Either they can do a home equity loan or a home equity line of credit (HELOC). For both of these options, the second mortgage is financed by the equity in their home. The difference between these two lies in the interest rates and how money is dispersed. The home equity loan is traditionally a closed-end loan, meaning interest is charged at a fixed rate with all of the loan proceeds disbursed to you in one lump payment. A HELOC is a line of credit for which you can determine how much you want to borrow, and interest is charged at a variable rate, like a credit card. The rates are usually higher for a second mortgage than your first, as these loans are typically for smaller amounts and are riskier for the lender.'
        },
        'PaymentIncrease-OLD': {
            Header: 'Payment Increase',
            Body:
                'Your monthly payments will increase if you choose this loan because you will pay it back sooner than your current loan.'
        },
        SecondLoanPayment: {
            Header: 'Second Loan Payment',
            Body:
                'The new monthly payment for a second loan is additional to your current mortgage payment.'
        },
        UseMinCCPmt: {
            Header: 'Use Minimum Payment',
            Body:
                'Paying the minimum amount on a credit card debt; it’s the least amount you owed where you can pay without penalties. Generally, your monthly payment is calculated up to 4% of the current outstanding balance.  Turn the toggle off to enter your own monthly payment or actual current credit card payment at a given month.'
        },
        EquityGained: {
            Header: 'Equity Gained',
            Body:
                'Equity gained is the amount of equity your home has accrued since a specific date.<br/><br/>The equity gained amount is calculated by money you have paid on your principal, plus any home appreciation.'
        },
        UseMyEquityDetails: {
            Header: 'Use My Equity',
            Body:
                'This estimate is the amount of available equity that you can take out of your home.<br/><br/>Talk to your loan advisor to learn more about your available home equity.'
        },
        MonthlySavings: {
            Header: 'Monthly Savings',
            Body: `Estimated savings is based on a payment example for a 30-year fixed rate mortgage amortized over 360 months.<br><br>Loan options are measured against your current mortgage.`
        },
        ShortenTermLoan: {
            Header: 'Shorten Term Loan',
            Body: `Choosing this loan option may enable you to own your home sooner because the new loan term is shorter thab the remaining time left on your current mortgage term.`
        },
        AssessedValuataion: {
            Header: 'Assessed Valuation',
            Body:
                "The assessed value of a home is a yearly dollar estimation of your home’s worth used for calculating property taxes. It is typically determined by government entities or assessor's offices. The assessed value is intended to reflect a percentage of the property's fair market value, the estimated price it would sell for in an open market.<br/><br/>In many jurisdictions, the assessed value is set at around 80% to 90% of the fair market value. However, the actual assessment ratio can vary depending on local regulations and practices. It's important to note that the relationship between assessed value and fair market value can differ from one area to another due to factors such as local policies and market conditions."
        },
        EstimatedMonthlyTax: {
            Header: 'Estimated Monthly Tax',
            Body:
                'Using the total from the tax year listed, we divided the number by 12 to get the estimated monthly tax.<br/><br/>When looking at mortgage options, it is important to consider the total monthly tax you will pay for a property, in addition to your principal and interest (monthly mortgage payment).'
        },
        EstimatedLoanCosts: {
            Header: 'Estimated Loan Costs',
            Body:
                'The estimated loan costs for each loan include the cost of the loan (points), if applicable, as well as closing costs. Closing costs may include, but are not limited to underwriting, processing, and loan origination fees.'
        },
        LoanApplicationThankYou: {
            Header: '',
            Body:
                'Thank you for entrusting us with your loan. Your submission has been received.<br/><br/>Please check your email for information about the next steps in the loan application process!'
        },
        MonthlyPaymentClarification: {
            Header: 'Est. Monthly Payment',
            Body:
                'The monthly payment shown includes principal and interest, but does not include taxes and insurance. Your monthly payment will be higher when factoring in these costs. Additionally, you may also have to pay private mortgage insurance (PMI) if your loan scenario has a down payment less than 20%.'
        },
        InterestRateAndAPR_StandAlone: {
            Header: 'Interest Rate & APR',
            Body: `<p>A mortgage interest rate is the percentage-based interest rate imposed by a lender on a mortgage loan used for purchasing a home or refinancing an existing one. The Annual Percentage Rate (APR) is a financial metric used to express the true cost of borrowing for a loan. It goes beyond the nominal interest rate by encompassing various fees and charges associated with the loan, giving borrowers a comprehensive view of what they will pay over the loan's term.</p>
            <p>Fixed interest rates remain the same for the life of your loan. Variable (or adjustable) interest rates can change over the life of your loan, usually after the initial fixed-rate period.</p>
            `
        },
        Points_StandAlone: {
            Header: 'Points',
            Body: `<p>Points are paid directly to the lender to lower your interest rate when you purchase a home. A point is equal to 1 percent of the loan amount. By paying more in points, you can potentially reduce your monthly mortgage payments and save money in interest over the life of the loan.</p>
            <p>Negative points are closing cost rebates to qualified borrowers that can reduce the upfront closing costs.</p>`
        },
        PrincipalAndInterest: {
            Header: 'Principal & Interest (P&I)',
            Body:
                'The estimated monthly payment shown includes principal and interest but does not include taxes and insurance. Your monthly payment may be higher when these costs are factored in. Additionally, you may also have to pay private mortgage insurance (PMI) if your loan scenario has a down payment of less than 20%.'
        },
        MortgageInsurance: {
            Header: 'Mortgage Insurance',
            Body:
                'Mortgage insurance, also known as private mortgage insurance (PMI), is a type of insurance that protects a lender from financial losses if a borrower defaults on their mortgage. It’s usually required when a borrower makes a down payment of less than 20% of the home’s purchase price, or when they choose a government-backed loan program.'
        },
        LoanProgram: {
            Header: 'Loan Program',
            Body: `<p>Loan programs often have specific names or designations that indicate their features, the institutions offering them, and their terms. Here are some common types and examples of loan program names:</p>
            <ul>
            <li>FNMA (Fannie Mae)<br/>Example: FNMA 30-Year Fixed</li>
            <li>FHA (Federal Housing Administration)<br/>Example: FHA 30-Year Fixed</li>
            <li>Adjustable-Rate Mortgages (ARMs)<br/>Example: 5/1 ARM</li>
            </ul>
            `
        },
        HomeInsuranceCalculation: {
            Header: 'Home Insurance Calculation',
            Body: 'Home insurance is estimated at 0.35% of home value annually. <br><br>Your actual insurance costs may vary based on your location, home features, coverage options, and insurance provider.'
        },
        MortgageInsuranceRequirements: {
            Header: 'Mortgage Insurance Requirements',
            Body: 'Mortgage insurance requirements vary by loan type:<br/><br/><strong>Conventional:</strong> Required when down payment is less than 20% (80% loan-to-value ratio)<br/><br/><strong>FHA:</strong> Always required regardless of down payment amount<br/><br/><strong>VA:</strong> Not applicable - VA loans do not require mortgage insurance'
        },
        InterestRateCalculation: {
            Header: 'Interest Rate',
            Body: 'The interest rate shown is based on current market averages for the selected loan type. Actual rates may vary based on your credit score, down payment, loan amount, and other factors. Get a personalized rate quote today!'
        },
        PropertyTaxCalculation: {
            Header: 'Property Tax Calculation',
            Body: 'Property tax is calculated as a percentage of your home\'s assessed value and varies by location. <br/><br/>The default rate shown (1.25%) is a national average. Actual property tax rates vary significantly by state, county, and municipality, ranging from 0.3% to over 3% annually. <br/><br/>Property taxes fund local services like schools, police, fire departments, and infrastructure. The monthly amount is calculated by dividing the annual property tax by 12.'
        },
    };

    constructor(
        public dialogRef: MatDialogRef<InfoPopup>,
        private router: Router,
        public LgvUserSvc: LgvUserService,
        public LgvDataHlpr: LgvDataHelper,
        private LgvClientSvc: LgvClientService,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public authMgr: LgvAuthMgrService
    ) {
        this.LgvClientSvc.PostPageClick(
            `Learn about - ${data.mode === 'generic' ? data.header : data.mode}`
        )
            .pipe(first())
            .subscribe();

        //console.log('InfoPopup');
        //console.log(data);

        // make assignments if data provided
        this.opportunity = !!data?.opportunity ? data?.opportunity : null;
        this.product = !!data?.product ? data?.product : null;
        this.quote = !!data?.quote ? data?.quote : null;
        this.quoteDetail = !!data?.quoteDetail ? data?.quoteDetail : null;
    }

    onNoClick(): void {
        this.dialogRef.close();
    }

    OnRequestCMA() {
        this.onNoClick();
        this.router.navigate(['/client/request-cma']);
    }

    abs(value: number): number {
        return Math.abs(value);
    }

    buyerFees(
        lenderFeeDetails?: LgvPricingModel.FeeDetail[]
    ): LgvPricingModel.FeeDetail[] {
        if (!lenderFeeDetails) {
            lenderFeeDetails = this.product?.quotesSearchResult
                ?.lenderFeeDetails;
        }
        return lenderFeeDetails?.filter(f => f.feePaidBy === 'Buyer');
    }

    calcEstTotalDue(quote): number {
        let buyerFees;
        if (quote?.lenderFeeDetails) {
            buyerFees = this.buyerFees(quote?.lenderFeeDetails);
        } else {
            buyerFees = this.buyerFees();
        }
        const totalFees = buyerFees?.reduce(
            (sum, fee) => sum + fee.feeTotalCalculatedAmount,
            0
        );
        return totalFees + this.quoteDetail.pointsCost;
    }

    clipText(inputStr: string, maxLen: number): string {
        return inputStr.length <= maxLen
            ? inputStr
            : inputStr.substring(0, maxLen) + '...';
    }
}
